import React from "react";
import Flatpickr from "react-flatpickr";
import { Link } from "react-router-dom";
import Select from "react-select";
import {
	Card,
	Badge,
	Table,
	ListGroup,
	ListGroupItem,
	Label,
	Input,
	Button,
} from "reactstrap";
import { HourlyActivityLog, Summary } from "store/audits/types";

type OptionType = { value: string; label: string };

export interface Props {
	staffName: string;
	shift: string;
	remarks: string[];
	summary: Summary;
	hourlyLogs: HourlyActivityLog[];
	selectedDate?: Date;
	selectedAction?: string;
	selectedModel?: string;
	selectedChildModel?: string;
	uniqueOnly?: boolean;

	onActionChange?: (value: string) => void;
	onModelChange?: (value: string) => void;
	onChildModelChange?: (value: string) => void;
	onDateChange?: (value: Date) => void;
	onToggleUniqueOnly?: (val: boolean) => void;
	onRefresh?: () => void;

	actionOptions?: string[];
	modelOptions?: string[];
	childModelOptions?: string[];

	checkInTime?: string;
	checkOutTime?: string;
}

const AuditLogCard: React.FC<Props> = ({
	staffName,
	shift,
	remarks,
	summary,
	hourlyLogs,
	selectedDate,
	selectedAction,
	selectedModel,
	selectedChildModel,
	uniqueOnly,
	onActionChange,
	onModelChange,
	onChildModelChange,
	onDateChange,
	onToggleUniqueOnly,
	onRefresh,
	actionOptions = [],
	modelOptions = [],
	childModelOptions = [],
	checkInTime,
	checkOutTime,
}) => {
	const buildSelectOptions = (values: string[]) => [
		{ value: "", label: "All" },
		...values.map((v) => ({
			value: v,
			label: toLabel(v),
		})),
	];

	const toLabel = (key: string): string => {
		if (!key) return "";

		return key
			.replace(/([a-z])([A-Z])/g, "$1 $2")     // camelCase → camel Case
			.replace(/[_\-]+/g, " ")                 // snake_case, kebab-case → snake case
			.toLowerCase()                           // everything to lowercase
			.replace(/\b\w/g, (c) => c.toUpperCase()); // capitalize first letter of each word
	};

	const handleReset = () => {
		onActionChange?.("");
		onChildModelChange?.("");
		onModelChange?.("");
		onToggleUniqueOnly?.(false);
	};

	return (
		<Card className="shadow p-4 mb-4 border-0 rounded">
			{/* Header Section */}
			<div className="mb-4 d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-4 bg-light px-3 py-3">
				{/* Staff Info */}
				<div className="p-3 rounded border bg-light">
					<h5 className="fw-bold mb-1 text-primary">{staffName}</h5>
					<p className="mb-1 text-muted">
						<i className="ri-time-line me-1" /> Shift: <span className="fw-medium">{shift}</span>
					</p>
					<p className="mb-1 text-muted">
						<i className="ri-login-box-line me-1" /> Check-in:
						<span className="fw-medium text-dark"> {checkInTime || "–"} </span>
					</p>
					<p className="mb-0 text-muted">
						<i className="ri-logout-box-line me-1" /> Check-out:
						<span className="fw-medium text-dark"> {checkOutTime || "–"} </span>
					</p>

				</div>

				{/* Summary */}
				<div className="d-flex flex-wrap justify-content-center align-items-center gap-3 flex-grow-1">
					{[
						{ label: "Points", value: summary.individual.total_points, team: summary.team.total_points, color: "text-success" },
						{ label: "Leads", value: summary.individual.total_leads, team: summary.team.total_leads, color: "text-primary" },
						{ label: "Activities", value: summary.individual.total_activities, team: summary.team.total_activities, color: "text-warning" },
					].map((metric, idx) => (
						<div key={idx} className="text-center bg-white border rounded p-3 px-4">
							<div className="text-muted small mb-1">{metric.label}</div>
							<div className={`fw-bold fs-4 ${metric.color}`}>{metric.value}</div>
							<div className="small text-muted">/ {metric.team} team</div>
						</div>
					))}
				</div>

				{/* Audit Date (Prominent Global Filter) */}
				<div className="d-flex flex-column align-items-end mt-3 mt-lg-0">
					<Label
						htmlFor="auditDate"
						className="fw-bold fs-5 text-dark mb-2"
						style={{ textAlign: "right", width: "100%" }}
					>
						<i className="ri-calendar-2-line me-1 text-danger" />
						Footprint Date
					</Label>
					<div className="text-muted small mb-2" style={{ textAlign: "right", width: "100%" }}>
						Filter logs by selected date
					</div>
					<div className="position-relative w-100" style={{ maxWidth: "260px" }}>
						<Flatpickr
							id="auditDate"
							className="form-control ps-5 pe-3 py-2 text-end"
							options={{
								dateFormat: "d M Y",
								maxDate: "today",
							}}
							value={selectedDate}
							onChange={(dates) => dates.length && onDateChange?.(dates[0])}
							placeholder="Select date"
						/>
						<i className="ri-calendar-2-line position-absolute top-50 start-0 translate-middle-y ps-3 text-primary" />
					</div>
				</div>

			</div>


			{/* Remarks */}
			{remarks.length > 0 && (
				<div className="mb-4 d-flex flex-column gap-1">
					<h5 className="fw-semibold">Remarks</h5>
					<ListGroup flush>
						{remarks.map((r, i) => (
							<ListGroupItem key={i} className="ps-0 py-1 border-0 text-dark bg-transparent d-flex align-items-center gap-1">
								<i className="ri-alert-fill fs-5 text-warning" />
								{r}
							</ListGroupItem>
						))}
					</ListGroup>
				</div>
			)}

			{/* Filters */}
			<div className="mb-4 bg-light px-3 py-3">
				<div className="d-flex justify-content-start align-items-center mb-2 gap-2">
					<h5 className="fw-semibold mb-0">Filters</h5>
					<Button
						color="danger"
						className="btn-label btn-sm"
						disabled={
							!(
								selectedAction ||
								selectedChildModel ||
								selectedModel ||
								selectedDate ||
								uniqueOnly
							)
						}
						onClick={handleReset}
					>
						<i className="ri-refresh-line label-icon align-middle fs-16 me-2"></i>
						Reset
					</Button>
				</div>

				<div className="row g-3">
					{/* Action */}
					<div className="col-md-3">
						<Label className="form-label text-muted fw-medium small mb-1">
							<i className="ri-flashlight-line me-1 text-primary" />
							Action
						</Label>
						<div className="text-muted small mb-1">Performed activity (e.g., create, update)</div>
						<Select
							options={buildSelectOptions(actionOptions)}
							value={{ value: selectedAction || "", label: selectedAction ? toLabel(selectedAction) : "All" }}
							onChange={(opt: OptionType) => onActionChange?.(opt?.value || "")}
							isClearable
							classNamePrefix="react-select"
						/>
					</div>

					{/* Primary Module */}
					<div className="col-md-3">
						<Label className="form-label text-muted fw-medium small mb-1">
							<i className="ri-database-2-line me-1 text-info" />
							Primary Module
						</Label>
						<div className="text-muted small mb-1">The table or entity directly affected</div>
						<Select
							options={buildSelectOptions(modelOptions)}
							value={{ value: selectedModel || "", label: selectedModel ? toLabel(selectedModel) : "All" }}
							onChange={(opt: OptionType) => onModelChange?.(opt?.value || "")}
							isClearable
							classNamePrefix="react-select"
						/>
					</div>

					{/* Related To */}
					{/* Related Module */}
					<div className="col-md-3">
						<Label className="form-label text-muted fw-medium small mb-1">
							<i className="ri-share-forward-line me-1 text-secondary" />
							Related Module
						</Label>
						<div className="text-muted small mb-1">Sub-entity under primary model (e.g., notes, tasks)</div>
						<Select
							options={buildSelectOptions(childModelOptions || [])}
							value={{ value: selectedChildModel || "", label: selectedChildModel ? toLabel(selectedChildModel) : "All" }}
							onChange={(opt: OptionType) => onChildModelChange?.(opt?.value || "")}
							isClearable
							classNamePrefix="react-select"
						/>
					</div>


					{/* Unique Leads */}
					<div className="col-md-3">
						<Label className="form-label text-muted fw-medium small mb-1 d-flex align-items-center">
							<i className="ri-user-voice-line me-1 text-success" />
							Unique Leads
						</Label>
						<div className="text-muted small mb-1">Show only one entry per lead</div>
						<div className="form-check form-switch form-switch-md">
							<Input
								type="checkbox"
								className="form-check-input"
								id="uniqueLeadSwitch"
								checked={uniqueOnly}
								onChange={(e) => onToggleUniqueOnly?.(e.target.checked)}
							/>
							<Label className="form-check-label ms-2 text-muted" htmlFor="uniqueLeadSwitch">
								Enable
							</Label>
						</div>
					</div>

				</div>
			</div>

			{/* Activity Table */}
			{/* Activity Table */}
			<div className="mb-5 d-flex flex-column gap-1">
				<div className="d-flex align-items-center justify-content-between">
					<h5 className="fw-semibold">Activities</h5>
					{onRefresh && (
						<Button
							color="link"
							className="p-0 text-decoration-none"
							title="Refresh data"
							onClick={onRefresh}
						>
							<i className="ri-refresh-line fs-3"></i>
						</Button>
				)}
				</div>

				{hourlyLogs.length === 0 ? (
					<div className="text-center p-5 border rounded bg-light">
						<i className="ri-file-info-line fs-2 text-muted mb-2 d-block" />
						<h6 className="fw-semibold text-muted">No activity logs found</h6>
						<p className="text-muted small mb-0">
							There are no audit logs available for the selected filters or date.
						</p>
					</div>
				) : (
					<div className="table-responsive rounded border">
						<Table className="align-middle table-nowrap mb-0">
							<thead className="table-light">
								<tr>
									<th style={{ width: "12%" }}>Timestamp</th>
									<th style={{ width: "15%" }}>Lead Name</th>
									<th style={{ width: "10%" }}>Type</th>
									<th style={{ width: "10%" }}>Action</th>
									<th style={{ width: "12%" }}>Time Spent</th>
									<th style={{ width: "25%" }}>Details</th>
									<th style={{ width: "8%" }}>Points</th>
									<th style={{ width: "8%" }}>Gap</th>
								</tr>
							</thead>
							<tbody>
								{hourlyLogs.map((log, logIdx) => (
									<React.Fragment key={logIdx}>
										<tr>
											<td colSpan={8} className="bg-light fw-semibold text-dark">
												<div className="d-flex justify-content-between align-items-center">
													<div>{log.hour_range}</div>
													<div className="text-muted small">
														{log.activities} activities, {log.leads} leads, {log.points} points
													</div>
													<div>
														<h5 className="mb-0">
															<Badge color={"primary"}>
																{log.points > 0 ? log.points : "–"}
															</Badge>
														</h5>
													</div>
												</div>
											</td>
										</tr>
										{log.logs.map((entry, entryIdx) => (
											<tr key={entryIdx}>
												<td>
													{entry.timestamp
														? new Date(entry.timestamp.replace(/\[.*\]$/, "")).toLocaleTimeString([], {
															hour: "2-digit",
															minute: "2-digit",
														})
														: "–"}
												</td>
												<td>
													{
														entry.record_name ?
															<Link
																to={`/${entry.model}/${entry.record}`}
																className="link-primary fw-semibold text-decoration-none link-underline-hover"
																title="Click to view lead details"
															>
																{entry.record_name}
															</Link> : "–"
													}
												</td>
												<td>{entry.child_model ? toLabel(entry.child_model) : entry.model ? toLabel(entry.model) : "–"}</td>
												<td>{toLabel(entry.action) || "–"}</td>
												<td>{entry.time_spent || "–"}</td>
												<td className="w-25 text-wrap text-break">
													<div
														className="overflow-auto"
														style={{ maxHeight: "100px" }}
														dangerouslySetInnerHTML={{ __html: entry.details || "–" }}
													/>
												</td>
												<td>
													<Badge color={entry.points > 0 ? "success" : entry.points === 0 ? "warning" : "secondary"}>
														{entry.points > 0 ? entry.points : "–"}
													</Badge>
												</td>
												<td>{entry.gap || "–"}</td>
											</tr>
										))}
									</React.Fragment>
								))}
							</tbody>
						</Table>
					</div>
				)}
			</div>

		</Card>
	);
};

export default AuditLogCard;
